'use client';

import { useFestivalContext } from '@/contexts/FestivalContext';
import { useUserStore } from '@/store/useUserStore';
import { getApiAuthHeaders } from '@/utils/api-helpers';
import { handleFormSubmitApiError } from '@/utils/form-submit-error';
import { cn } from '@/utils/tailwind-merge';
import { travelSchema } from '@/utils/validation-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import AccreditationSuccess from '../blocks/AccreditationSuccess';
import Button from '../ui/Button';
import Input from '../ui/Input';

type FormData = z.infer<typeof travelSchema>;
const URL = process.env.NEXT_PUBLIC_API_URL || 'https://image-nation.org/api-service';

const TravelForm = () => {
    const t = useTranslations();
    const locale = useLocale();
    const { user, session } = useUserStore();
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const { festival } = useFestivalContext();

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(travelSchema),
    });

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);

        if (!user) {
            setSubmitting(false);
            return;
        }
        try {
            const response = await fetch(`${URL}/form/accreditation/travel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': locale,
                    ...getApiAuthHeaders(session?.access_token),
                },
                body: JSON.stringify({
                    user_id: user.id,
                    formData: formData,
                    festival_id: festival?.id,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to submit the form');
            }

            reset();
            setIsSuccessful(true);
        } catch (error) {
            handleFormSubmitApiError(error, t);
        } finally {
            setSubmitting(false);
        }
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message && errors[fieldName]?.message !== 'Required'
            ? t(`FieldErrors.${errors[fieldName]?.message}`)
            : '';

    return isSuccessful ? (
        <div className={cn('w-full flex flex-col gap-7 justify-center items-center relative')}>
            <AccreditationSuccess className="mt-20 w-full" />
        </div>
    ) : (
        <form className="" onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* {Object.keys(errors).length > 0 && (
                <pre className="text-red-500 bg-gray-100 p-4 rounded">
                    {JSON.stringify(errors, null, 2)}
                </pre>
            )} */}
            <div className="flex flex-col gap-6 w-full">
                <div className="flex flex-col gap-6">
                    <Input
                        {...register('departureCountry')}
                        name="departureCountry"
                        id="departureCountry"
                        className="newsletter-input"
                        placeholder={t('AccreditationPage.departureCountry')}
                        color="white"
                        type="text"
                        error={getFieldError('departureCountry')}
                    />
                    <div className="grid grid-cols-1 xl:grid-cols-3 lg:gap-5 gap-3 w-full">
                        <Input
                            {...register('departureDate')}
                            name="departureDate"
                            id="departureDate"
                            className="newsletter-input"
                            placeholder={t('AccreditationPage.departureDate')}
                            color="white"
                            type="date"
                            error={getFieldError('departureDate')}
                        />
                        <Input
                            {...register('departureTime')}
                            name="departureTime"
                            id="departureTime"
                            className="newsletter-input"
                            placeholder={t('AccreditationPage.departureTime')}
                            color="white"
                            type="time"
                            error={getFieldError('departureTime')}
                        />
                        <Input
                            {...register('departureFlightTrainNumber')}
                            name="departureFlightTrainNumber"
                            id="departureFlightTrainNumber"
                            className="newsletter-input"
                            placeholder={t('AccreditationPage.flightTrainNumber')}
                            color="white"
                            type="text"
                            error={getFieldError('departureFlightTrainNumber')}
                        />
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-3 lg:gap-5 gap-3 w-full">
                        <Input
                            {...register('returnDate')}
                            name="returnDate"
                            id="returnDate"
                            className="newsletter-input"
                            placeholder={t('AccreditationPage.returnDate')}
                            color="white"
                            type="date"
                            error={getFieldError('returnDate')}
                        />
                        <Input
                            {...register('returnTime')}
                            name="returnTime"
                            id="returnTime"
                            className="newsletter-input"
                            placeholder={t('AccreditationPage.departureTime')}
                            color="white"
                            type="time"
                            error={getFieldError('returnTime')}
                        />
                        <Input
                            {...register('returnFlightTrainNumber')}
                            name="returnFlightTrainNumber"
                            id="returnFlightTrainNumber"
                            className="newsletter-input"
                            placeholder={t('AccreditationPage.flightTrainNumber')}
                            color="white"
                            type="text"
                            error={getFieldError('returnFlightTrainNumber')}
                        />
                    </div>
                </div>
                <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                    {t('AccreditationPage.yourStay')}
                </h4>
                <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-1 xl:grid-cols-2 lg:gap-5 gap-3 w-full">
                        <Input
                            {...register('hotelName')}
                            name="hotelName"
                            id="hotelName"
                            className="newsletter-input"
                            placeholder={t('AccreditationPage.hotelName')}
                            color="white"
                            type="text"
                            error={getFieldError('hotelName')}
                        />
                        <Input
                            {...register('hotelAddress')}
                            name="hotelAddress"
                            id="hotelAddress"
                            className="newsletter-input"
                            placeholder={t('AccreditationPage.hotelAddress')}
                            color="white"
                            type="text"
                            error={getFieldError('hotelAddress')}
                        />
                    </div>
                    <Input
                        {...register('howShouldWeContactYou')}
                        name="howShouldWeContactYou"
                        id="howShouldWeContactYou"
                        className="newsletter-input"
                        placeholder={t('AccreditationPage.howShouldWeContactYou')}
                        color="white"
                        type="text"
                        error={getFieldError('howShouldWeContactYou')}
                    />
                </div>

                <div className="flex flex-col">
                    <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                        {t('AccreditationPage.forInvitedGuests')}
                    </h4>
                    <div className="max-w-3xl my-5 flex flex-col gap-2 text-white font-helvetica-neue text-xl font-light">
                        <p>{t('AccreditationPage.forInvitedGuestsDescription')}</p>
                    </div>
                    <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                        {t('AccreditationPage.passportAndTravelInformation')}
                    </h4>
                </div>

                <div className="flex flex-col sm:grid grid-cols-1 md:grid-cols-3 w-full h-fit gap-6">
                    <div>
                        <Input
                            {...register('firstName')}
                            name="firstName"
                            id="firstName"
                            className="newsletter-input"
                            placeholder={t('Form.firstName')}
                            color="white"
                            type="text"
                            error={getFieldError('firstName')}
                        />
                        <p className="mt-2.5 font-helvetica-neue font-light text-lg text-white/60">
                            {t('AccreditationPage.asInPassport')}
                        </p>
                    </div>

                    <div>
                        <Input
                            {...register('lastName')}
                            name="lastName"
                            id="lastName"
                            className="newsletter-input"
                            placeholder={t('Form.lastName')}
                            color="white"
                            type="text"
                            error={getFieldError('lastName')}
                        />
                        <p className="mt-2.5 font-helvetica-neue font-light text-lg text-white/60">
                            {t('AccreditationPage.asInPassport')}
                        </p>
                    </div>
                    <Input
                        {...register('passportNumber')}
                        name="passportNumber"
                        id="passportNumber"
                        className="newsletter-input"
                        placeholder={t('Form.passportNumber')}
                        color="white"
                        type="text"
                        error={getFieldError('passportNumber')}
                    />
                    <Input
                        {...register('dateOfBirth')}
                        name="dateOfBirth"
                        id="dateOfBirth"
                        className="newsletter-input"
                        placeholder={t('Form.dateOfBirth')}
                        color="white"
                        type="date"
                        error={getFieldError('dateOfBirth')}
                    />
                    <Input
                        {...register('nationality')}
                        name="nationality"
                        id="nationality"
                        className="newsletter-input"
                        placeholder={t('Form.nationality')}
                        color="white"
                        type="text"
                        error={getFieldError('nationality')}
                    />
                    <Input
                        {...register('address')}
                        name="address"
                        id="address"
                        className="newsletter-input"
                        placeholder={t('Form.address')}
                        color="white"
                        type="text"
                        error={getFieldError('address')}
                    />
                    <Input
                        {...register('telephone')}
                        name="telephone"
                        id="telephone"
                        className="newsletter-input"
                        placeholder={t('Form.phone')}
                        color="white"
                        type="text"
                        error={getFieldError('telephone')}
                    />
                    <Input
                        {...register('email')}
                        name="email"
                        id="email"
                        className="newsletter-input"
                        placeholder={t('Form.email')}
                        color="white"
                        type="email"
                        error={getFieldError('email')}
                    />
                </div>

                <div className="flex flex-col">
                    <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                        {t('AccreditationPage.doYouHaveETA')}
                    </h4>
                    <div className="max-w-4xl my-5 flex flex-col gap-2 text-white font-helvetica-neue text-xl font-light">
                        <p>{t('AccreditationPage.etaAttention')}</p>
                        <p>
                            {t('AccreditationPage.etaLink')}
                            <br />
                            <a
                                href="https://www.canada.ca/en/immigration-refugees-citizenship/services/visit-canada/eta/apply.html"
                                className="text-accent"
                            >
                                https://www.canada.ca/en/immigration-refugees-citizenship/services/visit-canada/eta/apply.html
                            </a>
                        </p>
                        <p>{t('AccreditationPage.etaDescription')}</p>
                    </div>
                    <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                        {t('AccreditationPage.specialRequests')}
                    </h4>
                </div>
                <Input
                    {...register('specialRequests')}
                    name="specialRequests"
                    id="specialRequests"
                    className="newsletter-input"
                    placeholder={t('AccreditationPage.specialRequests')}
                    color="white"
                    type="text"
                    error={getFieldError('specialRequests')}
                />
                <p className="font-helvetica-neue text-white/60">
                    {t('AccreditationPage.specialRequestsDescription')}
                </p>
                <Button
                    type="submit"
                    className="bg-primary h-16 w-44 text-black text-3xl uppercase"
                    submitting={submitting}
                    disabled={submitting}
                    title={t('Form.save')}
                />
            </div>
        </form>
    );
};

export default TravelForm;
